import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

import { getAuthUserId } from "./AuthService";
import { firestore } from "./FirebaseService";

const hospitalsCollectionName = "hospitals";
const hospitalsCollection = collection(firestore, hospitalsCollectionName);
const usersCollection = collection(firestore, "users");

// In-memory cache so that synchronous consumers (getHospitalName,
// getHospitalShortName, form field options, etc.) can read the data without
// awaiting a Firestore fetch on every render. The cache is primed once by
// `preloadHospitals()` during app startup and invalidated after mutations.
let cache = null;

const toHospital = (docSnap) => {
  const data = docSnap.data() ?? {};
  return {
    id: docSnap.id,
    name: data.name ?? "",
    shortName: data.shortName ?? "",
    createdAt: data.createdAt ?? null,
    updatedAt: data.updatedAt ?? null,
    createdBy: data.createdBy ?? null,
  };
};

const sortByName = (a, b) =>
  (a.name ?? "").localeCompare(b.name ?? "", "es", { sensitivity: "base" });

const fetchAll = async () => {
  const q = query(hospitalsCollection, orderBy("name"));
  const snap = await getDocs(q);
  return snap.docs.map(toHospital).sort(sortByName);
};

export const preloadHospitals = async () => {
  if (cache) return cache;
  cache = await fetchAll();
  return cache;
};

export const getCachedHospitals = () => cache ?? [];

export const invalidateHospitalsCache = () => {
  cache = null;
};

export const getAllHospitals = async () => {
  cache = await fetchAll();
  return cache;
};

export const getHospitalById = async (hospitalId) => {
  if (!hospitalId) return null;
  const docRef = doc(firestore, hospitalsCollectionName, hospitalId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? toHospital(docSnap) : null;
};

export const getHospitalName = (hospitalId) => {
  if (!hospitalId) return "";
  const hospital = getCachedHospitals().find((h) => h.id === hospitalId);
  return hospital?.name ?? "";
};

export const getHospitalShortName = (hospitalId) => {
  if (!hospitalId) return "";
  const hospital = getCachedHospitals().find((h) => h.id === hospitalId);
  return hospital?.shortName ?? hospital?.name ?? "";
};

const idPattern = /^[a-z_]+$/;

const normalizePayload = (payload) => ({
  name: (payload?.name ?? "").trim(),
  shortName: (payload?.shortName ?? "").trim(),
});

export const addHospital = async (payload) => {
  const id = (payload?.id ?? "").trim();
  if (!id) {
    throw new Error("El ID es obligatorio");
  }
  if (!idPattern.test(id)) {
    throw new Error("El ID solo puede contener minúsculas y guiones bajos");
  }

  const { name, shortName } = normalizePayload(payload);
  if (!name) throw new Error("El nombre es obligatorio");
  if (!shortName) throw new Error("El nombre corto es obligatorio");

  const docRef = doc(firestore, hospitalsCollectionName, id);
  const existing = await getDoc(docRef);
  if (existing.exists()) {
    throw new Error(`Ya existe un hospital con el ID "${id}"`);
  }

  const createdBy = getAuthUserId();
  await setDoc(docRef, {
    name,
    shortName,
    ...(createdBy ? { createdBy } : {}),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  invalidateHospitalsCache();
  return id;
};

export const updateHospital = async (hospitalId, payload) => {
  if (!hospitalId) {
    throw new Error("ID de hospital no válido");
  }

  const { name, shortName } = normalizePayload(payload);
  if (!name) throw new Error("El nombre es obligatorio");
  if (!shortName) throw new Error("El nombre corto es obligatorio");

  const docRef = doc(firestore, hospitalsCollectionName, hospitalId);
  await updateDoc(docRef, {
    name,
    shortName,
    updatedAt: serverTimestamp(),
  });
  invalidateHospitalsCache();
  return true;
};

export const deleteHospital = async (hospitalId) => {
  if (!hospitalId) {
    throw new Error("ID de hospital no válido");
  }

  const usersQuery = query(usersCollection, where("hospital", "==", hospitalId));
  const usersSnap = await getDocs(usersQuery);
  if (!usersSnap.empty) {
    throw new Error(
      "No se puede eliminar el hospital porque hay usuarios asignados a él."
    );
  }

  await deleteDoc(doc(firestore, hospitalsCollectionName, hospitalId));
  invalidateHospitalsCache();
  return true;
};
